// Sons do round: contagem de largada, fim do tempo, nocaute e desistência.
//
// Sintetizados com a Web Audio API, como os efeitos da Roleta: nada de arquivo nem
// dependência. O som sai na saída de áudio do sistema, então a caixa ligada no P2
// se escolhe no Windows. O site não consegue escolher a saída sozinho, porque o
// setSinkId exige HTTPS.
//
// Quem chama isto é só a tela do juiz de round. A aba grande (/Cronometro) fica
// muda: com as duas tocando, o som sairia dobrado, e ela não recebe o clique que o
// navegador exige para liberar o áudio.
//
// Os sons são agendados no relógio do áudio, não em setTimeout: o Chrome atrasa os
// timers de aba em segundo plano, e o fim do round tocaria fora de hora.
//
// O contexto vive no módulo, e não no componente, para o som do nocaute não ser
// cortado quando a tela troca para a de Lutas.

import { CONTAGEM_MS } from '@/utils/cronometro'

// Atraso aceito para tocar um som que já devia ter começado. Cobre os milissegundos
// entre o clique em Iniciar e o navegador liberar o áudio.
const TOLERANCIA_MS = 150

let ctx = null
let saida = null
let agendados = []
let ultimoEstado = null
let chaveAtual = null

const criarContexto = () => {
  if (ctx) return ctx
  const Contexto = window.AudioContext || window.webkitAudioContext
  if (!Contexto) return null
  ctx = new Contexto()
  // o compressor segura o pico quando os acordes somam, para a caixa não distorcer
  const compressor = ctx.createDynamicsCompressor()
  compressor.connect(ctx.destination)
  saida = ctx.createGain()
  saida.gain.value = 0.9
  saida.connect(compressor)
  // Liberou depois de o estado já ter mudado (ex.: recarregou a página no meio do
  // round e só agora clicou): agenda o que ainda falta tocar. A checagem da chave
  // evita reagendar o que acabou de ser agendado, o que reiniciaria o primeiro bip.
  ctx.onstatechange = () => {
    if (ctx.state !== 'running') {
      chaveAtual = null // relógio do áudio parou: a agenda atual não vale mais
      return
    }
    if (ultimoEstado && chaveDe(ultimoEstado) !== chaveAtual) agendar()
  }
  return ctx
}

const nota = (inicio, freq, duracao, { volume = 0.5, tipo = 'sawtooth', lista = null, soltura = duracao * 0.3 } = {}) => {
  const osc = ctx.createOscillator()
  const ganho = ctx.createGain()
  osc.type = tipo
  osc.frequency.setValueAtTime(freq, inicio)
  ganho.gain.setValueAtTime(0.0001, inicio)
  ganho.gain.exponentialRampToValueAtTime(volume, inicio + 0.012)
  ganho.gain.setValueAtTime(volume, inicio + duracao - soltura)
  ganho.gain.exponentialRampToValueAtTime(0.0001, inicio + duracao)
  osc.connect(ganho).connect(saida)
  osc.start(inicio)
  osc.stop(inicio + duracao + 0.02)
  if (lista) lista.push({ osc, ganho })
}

const acorde = (inicio, freqs, duracao, opcoes) => {
  freqs.forEach(f => nota(inicio, f, duracao, { volume: 0.32, ...opcoes }))
}

const tocarBip = (t, lista) => nota(t, 880, 0.18, { volume: 0.6, tipo: 'square', lista })

// Depois do 3, 2, 1: mesmo tom dos bips, sem variar até o fim do segundo.
const tocarBipLargada = (t, lista) => nota(t, 880, 1, { volume: 0.6, tipo: 'square', lista, soltura: 0.03 })

// Fim do tempo: o mesmo timbre e volume dos bips da largada, mas descendo de tom e
// terminando longo. A largada sobe a tensão no mesmo tom; aqui a queda diz que acabou.
const tocarFim = (t, lista) => {
  const bip = (inicio, freq, duracao) => nota(inicio, freq, duracao, { volume: 0.6, tipo: 'square', lista, soltura: 0.03 })
  bip(t, 880, 0.18)
  bip(t + 0.3, 660, 0.18)
  bip(t + 0.6, 440, 1)
}

// Um cacarejo: voz nasalada com a altura variando, filtrada para soar como bico.
const cacarejo = (t, freqs, duracao, volume) => {
  const osc = ctx.createOscillator()
  const filtro = ctx.createBiquadFilter()
  const ganho = ctx.createGain()
  osc.type = 'sawtooth'
  osc.frequency.setValueCurveAtTime(new Float32Array(freqs), t, duracao)
  filtro.type = 'bandpass'
  filtro.frequency.setValueAtTime(1500, t)
  filtro.Q.setValueAtTime(3, t)
  ganho.gain.setValueAtTime(0.0001, t)
  ganho.gain.exponentialRampToValueAtTime(volume, t + 0.012)
  ganho.gain.setValueAtTime(volume, t + duracao * 0.75)
  ganho.gain.exponentialRampToValueAtTime(0.0001, t + duracao)
  osc.connect(filtro).connect(ganho).connect(saida)
  osc.start(t)
  osc.stop(t + duracao + 0.02)
}

// "Pó, pó, pó, pó-CÓÓÓ": três curtos e o grito final subindo e caindo.
const tocarGalinha = (t) => {
  [0, 0.17, 0.34].forEach((d) => cacarejo(t + d, [620, 780, 540], 0.09, 1))
  cacarejo(t + 0.58, [600, 720], 0.08, 1)
  cacarejo(t + 0.68, [760, 1250, 1380, 1180, 820], 0.5, 1)
}

const cancelarAgendados = () => {
  agendados.forEach(({ osc, ganho }) => {
    try {
      osc.stop()
    } catch (e) { /* já tinha terminado */ }
    ganho.disconnect()
  })
  agendados = []
}

const agendar = () => {
  cancelarAgendados()
  chaveAtual = null
  const estado = ultimoEstado
  if (!estado || !estado.rodando) {
    chaveAtual = chaveDe(estado)
    return
  }
  // Áudio ainda bloqueado: o relógio dele está parado, não há como agendar. O
  // onstatechange refaz isto assim que liberar.
  if (!criarContexto() || ctx.state !== 'running') return

  const agora = Date.now()
  const base = ctx.currentTime
  const quando = (epochMs, tocar) => {
    const atraso = epochMs - agora
    if (atraso < -TOLERANCIA_MS) return
    tocar(base + Math.max(atraso, 0) / 1000 + 0.01, agendados)
  }

  if (estado.largadaEm) {
    const inicio = estado.largadaEm - CONTAGEM_MS
    quando(inicio, tocarBip)
    quando(inicio + 1000, tocarBip)
    quando(inicio + 2000, tocarBip)
    quando(estado.largadaEm, tocarBipLargada)
  }
  quando(estado.fimEm, tocarFim)
  chaveAtual = chaveDe(estado)
}

const chaveDe = (estado) => {
  if (!estado || !estado.rodando) return 'parado'
  return estado.fimEm + ':' + (estado.largadaEm || 0)
}

// Faz os sons agendados baterem com o estado do cronômetro. Pode ser chamada à
// vontade: a mesma mudança chega de novo pelo canal e pelo evento 'storage', e
// reagendar cortaria a nota que está tocando.
export const sincronizar = (estado) => {
  ultimoEstado = estado
  if (chaveDe(estado) === chaveAtual) return
  agendar()
}

// Saiu da tela do juiz: nada de tocar fim de round sem ninguém controlando. Não
// corta o que já está soando fora da agenda (o toque do nocaute).
export const parar = () => {
  ultimoEstado = null
  chaveAtual = null
  cancelarAgendados()
}

// Libera o áudio. Precisa rodar dentro de um clique ou tecla, senão o navegador
// mantém o contexto suspenso.
export const desbloquear = () => {
  if (!criarContexto()) return Promise.resolve()
  return ctx.state === 'suspended' ? ctx.resume() : Promise.resolve()
}

export const bloqueado = () => !ctx || ctx.state !== 'running'

const tocarAgora = (tocar) => {
  desbloquear().then(() => {
    if (ctx) tocar(ctx.currentTime + 0.02)
  }).catch(() => { /* navegador recusou: fica sem som, a luta segue */ })
}

// Três buzinadas graves: bem diferente da largada e do fim do tempo.
export const tocarNocaute = () => tocarAgora((t) => {
  [0, 0.4, 0.8].forEach((d) => acorde(t + d, [146.83, 220], 0.32, { volume: 0.5 }))
})

export const tocarDesistencia = () => tocarAgora(tocarGalinha)

// Para conferir na caixa o toque de fim do round sem esperar o tempo acabar.
export const tocarTesteFim = () => tocarAgora((t) => tocarFim(t))

// Para conferir a caixa antes de começar: um bip curto e o contínuo da largada.
export const tocarTeste = () => tocarAgora((t) => {
  tocarBip(t)
  tocarBipLargada(t + 0.5)
})
